import { useEffect, useRef, useState, type RefObject } from 'react';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { DEFAULT_OVERVIEW_TARGETS_SETTINGS, OVERVIEW_VITALS, type OverviewTargetsSettings, type OverviewVital, type TargetPeriod } from '../../shared/overview';
import { GOAL_METRICS, type GoalMetric } from '../../shared/stats';
import { api } from '../lib/api';
import { useIsMobile } from '../lib/useIsMobile';
import { Sheet, SheetButton, type SheetDismissReason } from './Sheet';

export const VITAL_LABELS: Record<OverviewVital, string> = {
  open_tasks: 'Open tasks', active_projects: 'Active projects', outstanding: 'Outstanding', overdue: 'Overdue',
  revenue_ex: 'Revenue ex GST', leads: 'Leads', proposals_delivered: 'Proposals & quotes', jobs_won: 'Jobs won',
};

type PeriodOption = { period: TargetPeriod; label: string };
type ChooserPeriods = Partial<Record<OverviewVital, PeriodOption[]>>;
const BOTH_PERIODS: TargetPeriod[] = ['month', 'quarter'];

// A lone available goal always stays on. Only widen a stored preference
// that would hide it; one that already includes it is kept as saved.
function initialDraft(settings: OverviewTargetsSettings, periods: ChooserPeriods): OverviewTargetsSettings {
  const target_periods = { ...settings.target_periods };
  for (const metric of settings.targets) {
    const options = periods[metric];
    if (options?.length === 1 && !(target_periods[metric] ?? BOTH_PERIODS).includes(options[0].period)) {
      target_periods[metric] = [...BOTH_PERIODS];
    }
  }
  return { ...settings, target_periods };
}

function ChooserRow({ id, checked, value, periods = [], selectedPeriods, toggle, togglePeriod }: {
  id: OverviewVital; checked: boolean; value: string; periods?: PeriodOption[]; selectedPeriods?: TargetPeriod[];
  toggle: () => void; togglePeriod?: (period: TargetPeriod) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const showChips = checked && !!togglePeriod && periods.length > 0;
  const periodLabel = periods.map(p => p.label).join(' · ');
  return <div ref={setNodeRef} className={`chooser-row${togglePeriod ? ' target-period-row' : ''}${checked ? '' : ' off'}${isDragging ? ' dragging' : ''}`}
    style={{ transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined, transition }}>
    <button type="button" className="handle" aria-label={`Reorder ${VITAL_LABELS[id]}`} {...attributes} {...listeners}><svg viewBox="0 0 10 14" fill="currentColor" aria-hidden="true">{[3, 7, 11].flatMap(y => [3, 7].map(x => <circle key={`${x}-${y}`} cx={x} cy={y} r="1" />))}</svg></button>
    <label><input type="checkbox" checked={checked} onChange={toggle} /><span aria-hidden="true" className={`cbx${checked ? ' on' : ''}`} /><span className="chooser-metric">{VITAL_LABELS[id]}{!showChips && periodLabel && <span className="chooser-period"> · {periodLabel}</span>}</span></label>
    {showChips && <span className="chooser-period-chips" role="group" aria-label={`${VITAL_LABELS[id]} periods`}>
      {periods.map(({ period, label }) => <button type="button" key={period} className="chooser-period-chip"
        aria-pressed={periods.length === 1 || (selectedPeriods ?? BOTH_PERIODS).includes(period)} disabled={periods.length === 1}
        title={periods.length === 1 ? `Only ${label} has a ${VITAL_LABELS[id].toLowerCase()} target` : undefined}
        onClick={() => togglePeriod(period)}>{label}</button>)}
    </span>}
    <span className={`val${id === 'revenue_ex' || id === 'outstanding' ? ' gold' : id === 'overdue' ? ' danger' : ''}`}>{value}</span>
  </div>;
}

/** Shared by the desktop listeners and Sheet's dismissal gate. Refs make a
 * pending save/drag visible immediately, before React schedules a render. */
export function canDismissOverviewChooser(state: { saving: boolean; dragging: boolean }, reason: SheetDismissReason): boolean {
  return !state.saving && (reason !== 'escape' || !state.dragging);
}

/** Exported lifecycle seam for the repository's DOM-free test environment. */
export function bindOverviewChooserDismissal({ host, panel, trigger, canDismiss, onClose, mobile }: {
  host: Pick<Document, 'addEventListener' | 'removeEventListener'>;
  panel: HTMLElement | null; trigger: HTMLElement | null;
  canDismiss: (reason: SheetDismissReason) => boolean; onClose: () => void; mobile: boolean;
}): () => void {
  const escape = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && canDismiss('escape')) onClose();
  };
  const outside = (event: PointerEvent) => {
    if (canDismiss('backdrop') && !panel?.contains(event.target as Node) && !trigger?.contains(event.target as Node)) onClose();
  };
  if (!mobile) {
    panel?.focus();
    // Check before dnd-kit's bubbling Escape handler resets its drag state.
    host.addEventListener('keydown', escape, { capture: true });
    host.addEventListener('pointerdown', outside);
  }
  return () => {
    if (!mobile) {
      host.removeEventListener('keydown', escape, { capture: true });
      host.removeEventListener('pointerdown', outside);
    }
    trigger?.focus({ preventScroll: true });
  };
}

/** A draft is isolated until Done; saving replaces the single shared settings row. */
export function OverviewChooser({ kind, settings, values, periods = {}, onSaved, onClose, triggerRef, onSavingChange }: {
  kind: 'vitals' | 'targets'; settings: OverviewTargetsSettings; values: Partial<Record<OverviewVital, string>>;
  periods?: ChooserPeriods;
  triggerRef: RefObject<HTMLButtonElement>; onSavingChange?: (saving: boolean) => void;
  onSaved: (settings: OverviewTargetsSettings) => void; onClose: () => void;
}) {
  const [draft, setDraft] = useState(() => kind === 'targets' ? initialDraft(settings, periods) : settings);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mobile = useIsMobile();
  const ref = useRef<HTMLDivElement>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const busy = useRef({ saving: false, dragging: false });
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const canDismiss = (reason: SheetDismissReason) => canDismissOverviewChooser(busy.current, reason);
  useEffect(() => bindOverviewChooserDismissal({
    host: document, panel: ref.current, trigger: triggerRef.current, mobile,
    canDismiss: reason => canDismissOverviewChooser(busy.current, reason),
    onClose: () => closeRef.current(),
  }), [mobile, triggerRef]);
  const selected: OverviewVital[] = draft[kind];
  const available = (kind === 'vitals' ? OVERVIEW_VITALS : GOAL_METRICS).filter(key => !selected.includes(key));
  const ordered = [...selected, ...available];
  const tracked = draft.targets.reduce((sum, key) => sum + (periods[key]?.filter(({ period }) =>
    (draft.target_periods?.[key] ?? BOTH_PERIODS).includes(period)).length ?? 0), 0);
  const periodLabels = [...new Set(Object.values(periods).filter(options => options.length > 1).flatMap(options => options.map(p => p.label)))].join(' or ');
  const maxKey = kind === 'vitals' ? 'vitals_max' : 'targets_max';
  const setSelected = (next: OverviewVital[]) => setDraft({ ...draft, [kind]: next });
  const toggle = (key: OverviewVital) => {
    const next = selected.includes(key) ? selected.filter(item => item !== key) : [...selected, key];
    setDraft({ ...draft, [kind]: next, ...(kind === 'targets' && !selected.includes(key)
      ? { target_periods: { ...draft.target_periods, [key]: [...BOTH_PERIODS] } } : {}) });
  };
  const togglePeriod = (metric: GoalMetric, period: TargetPeriod) => {
    if (periods[metric]?.length === 1) return;
    const current = draft.target_periods?.[metric] ?? BOTH_PERIODS;
    const next = current.includes(period) ? current.filter(p => p !== period) : [...current, period];
    setDraft({ ...draft, targets: next.length ? draft.targets : draft.targets.filter(key => key !== metric),
      target_periods: { ...draft.target_periods, [metric]: next.length ? next : [...BOTH_PERIODS] } });
  };
  const reset = () => setDraft(kind === 'vitals'
    ? { ...draft, vitals: [...DEFAULT_OVERVIEW_TARGETS_SETTINGS.vitals], vitals_max: 4 }
    : { ...draft, targets: [...DEFAULT_OVERVIEW_TARGETS_SETTINGS.targets], targets_max: 3, targets_order: 'pace', target_periods: {} });
  const save = async () => {
    if (busy.current.saving) return;
    busy.current.saving = true;
    setSaving(true); onSavingChange?.(true); setError(null);
    try {
      const saved = await api<OverviewTargetsSettings>('/api/admin/overview/targets-settings', { method: 'PUT', json: draft });
      onSaved(saved); onClose();
    } catch { setError('Could not save your choices. Try again.'); }
    finally { busy.current.saving = false; setSaving(false); onSavingChange?.(false); }
  };
  const actions = <><button type="button" className="reset" disabled={saving} onClick={reset}>{mobile ? 'Reset' : 'Reset to default'}</button>
    <button type="button" className="btn primary sm" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Done'}</button></>;
  const rows = (keys: readonly OverviewVital[]) => <div className="chooser-list">{keys.map(key => <ChooserRow key={key} id={key}
    checked={selected.includes(key)} value={values[key] ?? '—'} periods={periods[key]} selectedPeriods={draft.target_periods?.[key as GoalMetric]}
    toggle={() => toggle(key)} togglePeriod={kind === 'targets' ? period => togglePeriod(key as GoalMetric, period) : undefined} />)}</div>;
  const title = kind === 'vitals' ? 'Vitals tiles' : 'Targets on Overview';
  const content = <div ref={ref} tabIndex={mobile ? undefined : -1} role={mobile ? undefined : 'dialog'} aria-label={mobile ? undefined : title}
    className={`chooser overview-chooser${mobile ? ' in-sheet' : ''}`}>
    {!mobile && <div className="chooser-head"><span className="chooser-title">{kind === 'vitals' ? 'Vitals tiles' : 'Targets on Overview'}</span>
      <span className="chooser-sub">{kind === 'vitals' ? `${selected.length} of 8 on` : 'Drag to reorder'}</span></div>}
    <fieldset disabled={saving}>
      <DndContext sensors={sensors} collisionDetection={closestCenter}
        onDragStart={() => { busy.current.dragging = true; }}
        onDragCancel={() => { busy.current.dragging = false; }}
        onDragEnd={({ active, over }) => {
        busy.current.dragging = false;
        if (!over || active.id === over.id) return;
        const next = arrayMove(ordered, ordered.indexOf(active.id as OverviewVital), ordered.indexOf(over.id as OverviewVital));
        setSelected(next.filter(key => selected.includes(key)));
      }}><SortableContext items={ordered} strategy={verticalListSortingStrategy}>
        <div className="chooser-sec">{kind === 'vitals' ? 'Shown, in order' : 'Goal metrics'}</div>
        {rows(kind === 'vitals' ? selected : ordered)}
        {kind === 'vitals' && <><div className="chooser-sec">Available</div>{rows(available)}</>}
      </SortableContext></DndContext>
      {kind === 'targets' && <div className="chooser-ctl"><span className="lbl">Order</span><div className="seg">
        {(['pace', 'listed'] as const).map(order => <button type="button" key={order} aria-pressed={draft.targets_order === order}
          className={`seg-item${draft.targets_order === order ? ' active' : ''}`} onClick={() => setDraft({ ...draft, targets_order: order })}>
          {order === 'pace' ? 'Most behind' : 'As listed'}</button>)}
      </div></div>}
      <div className="chooser-ctl"><span className="lbl">Max shown</span><div className="cstep">
        <button type="button" aria-label="Show fewer" disabled={draft[maxKey] <= 1} onClick={() => setDraft({ ...draft, [maxKey]: draft[maxKey] - 1 })}>−</button>
        <span aria-live="polite">{draft[maxKey]}</span>
        <button type="button" aria-label="Show more" disabled={draft[maxKey] >= (kind === 'vitals' ? 6 : 8)} onClick={() => setDraft({ ...draft, [maxKey]: draft[maxKey] + 1 })}>+</button>
      </div></div>
      <div className="chooser-hint">{kind === 'vitals'
        ? 'Goal metrics show the live value for the current month, with progress to target underneath when one is set. Up to 6 tiles on desktop; phones wrap two per row.'
        : `Shows ${Math.min(draft.targets_max, tracked)} of ${tracked} tracked. Each lit chip is one card on the strip, so untick ${periodLabels || 'a period'} to drop that period. A metric with a target for only one period has a single chip that stays on. “Most behind” shows the furthest-behind-pace targets first.`}</div>
    </fieldset>
    {error && <p className="chooser-hint danger" role="alert">{error}</p>}
    {!mobile && <div className="chooser-foot">{actions}</div>}
  </div>;
  return mobile ? <Sheet open variant="half" title={title} className="overview-chooser-sheet"
    context={kind === 'vitals' ? `${selected.length} of 8 on` : 'Drag to reorder'}
    canDismiss={canDismiss} onClose={() => closeRef.current()}
    leftAction={<SheetButton disabled={saving} onClick={reset}>Reset</SheetButton>}
    rightAction={<SheetButton tone="accent" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Done'}</SheetButton>}>
    {content}
  </Sheet> : content;
}
